import React, { SelectHTMLAttributes, useMemo } from 'react';
import colors from '../../helpers/colors';
import componentProps from '../../helpers/componentProps';
import { applyComponentTheme } from '../../theme';
import {
  StyledDropdown,
  StyledDropdownContainer,
  StyledDropdownList,
  StyledDropdownListItem,
  StyledIcon,
  StyledLabel,
  StyledLabelContainer,
} from './styles';
import useSelectControl from './useSelectControl';

export type Option = { name: string; value: string };

// TODO: Investigation - Check for this why the list items render styleless on hover / Test if it still appears like that
export type DropdownProps = {
  id: string;
  ariaLabel: string;
  options: Option[];
  placeholder?: string;
  label?: string;
  value?: string;
  handleValue?: (value: string) => void;
} & SelectHTMLAttributes<HTMLSelectElement>;

const Dropdown: React.FC<DropdownProps> = ({
  id,
  options,
  ariaLabel,
  placeholder,
  label,
  value,
  handleValue,
  ...props
}) => {
  const {
    dropdownRef,
    addOptionRef,
    dropdownOpen,
    changeValue,
    mouseHover,
    preSelectedValue,
    selectedValue,
    handleKeyboardControls,
    toggleDropdownOpen,
    setMouseHover,
    isKeyboardControlStarted,
  } = useSelectControl({
    options,
    value,
    handleValue,
  });

  const selectedName = useMemo(
    () =>
      options.find((option) => option.value === selectedValue)?.name ??
      placeholder ??
      'Please select',
    [selectedValue, options, placeholder]
  );

  return (
    <StyledDropdownContainer
      ref={dropdownRef}
      role="listbox"
      aria-label={ariaLabel}
      data-testid="dropdown">
      <StyledLabelContainer>
        {label && <StyledLabel>{label}</StyledLabel>}
      </StyledLabelContainer>
      <StyledDropdown
        onClick={(e) => {
          toggleDropdownOpen();
          e.currentTarget.focus();
        }}
        onKeyDown={handleKeyboardControls}
        id={id}
        data-testid="dropdown-input"
        tabIndex={0}
        isOpen={dropdownOpen}
        data-toggle="dropdown"
        {...componentProps(props)}>
        <p data-testid="input-text">{selectedName}</p>
        <StyledIcon
          name={dropdownOpen ? 'arrow-up' : 'arrow-down'}
          size={'x-small'}
          color={colors.core01}
        />
      </StyledDropdown>
      <StyledDropdownList
        aria-labelledby={id}
        isDropdownOpen={dropdownOpen}
        data-testid="dropdown-list">
        {options.map((option, index) => (
          <StyledDropdownListItem
            data-testid="dropdown-option"
            role="option"
            ref={addOptionRef}
            key={`option_${index}`}
            onMouseEnter={() => {
              if (!isKeyboardControlStarted) {
                setMouseHover(true);
              }
            }}
            onMouseLeave={() => setMouseHover(false)}
            isValuePreselected={option.value === preSelectedValue}
            isValueSelected={option.value === selectedValue}
            isKeyboardControlStarted={isKeyboardControlStarted}
            isMouseOver={mouseHover}
            onClick={() => {
              changeValue(option.value);
            }}>
            {option.name}
          </StyledDropdownListItem>
        ))}
      </StyledDropdownList>
    </StyledDropdownContainer>
  );
};

export default applyComponentTheme(Dropdown, 'Dropdown');
